/**
 *
 * UserReviews
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import Numeral from 'numeral';
import VisibilitySensor from 'react-visibility-sensor';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import { useScrollData } from 'scroll-data-hook';
import { constructUserReviewsData } from './utils';
import UserReview from './UserReview';
import WriteReview from './WriteReview';

const styles = theme => ({
  ratingLen: {
    marginTop: 16,
    marginBottom: 34,
  },
  root: {
    padding: '20px 0',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 18px',
    },
  },
});

function UserReviews(props) {
  const {
    classes,
    data = {},
    items,
    id,
    rate,
    review,
    sendReview,
    isAdmin,
    cmUserReviews = [],
    type,
  } = props;
  const { scrolling } = useScrollData();
  const [userReviewsSiteCopy] = useSiteCopySelector(['user-reviews']);
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const formattedData = [
    ...constructUserReviewsData(data),
    ...cmUserReviews,
  ].filter(item => !_isEmpty(_get(item, 'person')));

  if (_isEmpty(formattedData)) return null;

  const finalData = formattedData.sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );
  const itemLen = finalData.length;

  const visibilityChange = isVisible => {
    const {
      fields: { name, title },
    } = items[0];
    if (isVisible && scrolling) {
      Mixpanel.track('Resource Page - ScrollTo', {
        path: window.location.pathname,
        section: 'user-review-panel',
        type,
        name: name || title,
        entryId: id,
      });
    }
  };

  return (
    <section id="user-reviews-panel">
      <Grid container className={classes.root}>
        <Grid item xs={12}>
          <Grid container direction="row" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" color="textPrimary">
                {_get(userReviewsSiteCopy, 'pageHeader')}
              </Typography>
            </Grid>
            {allowSignIn && (
              <Grid item>
                <WriteReview
                  id={id}
                  isAdmin={isAdmin}
                  data={items}
                  rate={rate}
                  review={review}
                  sendReview={sendReview}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <VisibilitySensor
          onChange={visibilityChange}
          scrollCheck
          scrollDelay={500}
        >
          <Grid item xs={12}>
            <Typography
              variant="body1"
              color="textPrimary"
              className={classes.ratingLen}
            >
              {itemLen === 0
                ? _get(userReviewsSiteCopy, 'pageCopy.emptyReviewsMessage', '')
                : `${Numeral(itemLen).format('0a')} ${
                    itemLen > 1
                      ? _get(
                          userReviewsSiteCopy,
                          'pageCopy.reviewLabelPlural',
                          '',
                        )
                      : _get(
                          userReviewsSiteCopy,
                          'pageCopy.reviewLabelSingular',
                          '',
                        )
                  }`}
            </Typography>
          </Grid>
        </VisibilitySensor>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {finalData.map(el => (
              <UserReview
                data={el}
                key={`${_get(el, 'person')}/${_get(el, 'date')}`}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </section>
  );
}

UserReviews.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object,
  items: PropTypes.array,
  isAdmin: PropTypes.bool,
  rate: PropTypes.func,
  review: PropTypes.object,
  sendReview: PropTypes.func,
  id: PropTypes.string,
  cmUserReviews: PropTypes.array,
  type: PropTypes.string,
};

export default withStyles(styles)(UserReviews);
